import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { useShowPlayDemoData } from '@bbkAdminComponents/play/play-context';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { selectFromResultTyped } from '@bbkAdminRedux/redux_utils';
import {
  selectPlaysLibNav,
  useGetDemoSuffix,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import {
  selectExperiencesAudiencesPlaysReportFilter,
  useResolvePlaysFromDeliveryFilter,
} from '@bbkAdminRedux/rtkq/app-persist/reports-filter';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import {
  DEFAULT_BRAND_ID,
  useGetSelectedBrand,
} from '@bbkAdminRedux/rtkq/brands.slice';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import { playSlice, PlayType, type Play } from '@bbkAdminRedux/rtkq/play.slice';
import {
  reportsSlice,
  ReportType,
  type GetReportV2Args,
  type LifecycleEntityPlayMetricsReport,
  type LifecyclePlayReport,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { useGetProductLicense } from '@bbkAdminUtils/plans';

export const usePlaysData = (additionalPlays?: Play[]) => {
  const appKey = useSelector(selectAppId);
  const brandId = useGetSelectedBrand(appKey) ?? DEFAULT_BRAND_ID;
  const { selectedPlayType } = useSelector(selectPlaysLibNav);
  const showDemoData = useShowPlayDemoData();

  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;
  const growthV2License = productLicense.growthV2License;

  const { startDate: start, endDate: end } = useSelectDatePicker();
  const request = {
    report_type: ReportType.lifecycle_play_offer_metrics,
    parameters: {
      group_by_period: 'all',
    },
  };
  const suffix = useGetDemoSuffix();
  const reportFilters = useSelector(
    selectExperiencesAudiencesPlaysReportFilter(suffix)
  );
  const deliveryFilters = useResolvePlaysFromDeliveryFilter();
  const args: GetReportV2Args = {
    appKey,
    start,
    end,
    report_type_requests: [request],
    filters: [reportFilters],
    context: showDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  };
  const opts = {
    skip: !(agsLicense || growthV2License) || !appKey || !start || !end,
    selectFromResult: selectFromResultTyped<LifecyclePlayReport>,
  };

  const {
    data: playMetrics,
    isFetching: isPlaysReportFetching,
    error: playMetricsError,
  } = reportsSlice.endpoints.getReportV2.useQuery(args, opts);

  const requestEntityMetrics = {
    report_type: ReportType.lifecycle_entity_metrics,
    parameters: {
      entity_type: 'play',
      group_by: 'play_id',
      group_by_period: 'all',
    },
  };
  const argsEntityMetrics: GetReportV2Args = {
    appKey,
    start,
    end,
    report_type_requests: [requestEntityMetrics],
    filters: [reportFilters, deliveryFilters],
    context: showDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  };
  const optsEntityMetrics = {
    skip: !(agsLicense || growthV2License) || !appKey || !start || !end,
    selectFromResult: selectFromResultTyped<LifecycleEntityPlayMetricsReport>,
  };
  const {
    data: entityMetrics,
    isFetching: isEntityReportFetching,
    error: entityMetricsError,
  } = reportsSlice.endpoints.getReportV2.useQuery(
    argsEntityMetrics,
    optsEntityMetrics
  );
  const {
    data: playsListRaw = [],
    isFetching: isPlaysFetching,
    isLoading: isPlaysLoading,
  } = playSlice.endpoints.listPlays.useQuery({
    appKey,
    brandId,
    context: showDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  });

  const playsList = useMemo(() => {
    const mergedPlays = [...playsListRaw, ...(additionalPlays || [])];
    return _.sortBy(mergedPlays, [(x) => x.priority]);
  }, [playsListRaw, additionalPlays]);

  const {
    data: archivedPlaysList = [],
    isFetching: isArchivedPlaysFetching,
    isLoading: isArchivedPlaysLoading,
  } = playSlice.endpoints.listPlays.useQuery({
    appKey,
    brandId,
    active: false,
    context: showDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  });

  const activePlayIds = useMemo(
    () => playsList.filter((p) => p.active).map((p) => p.id),
    [playsList]
  );

  const filterBySelectedType = useCallback(
    (x: Play) => {
      const regularTypes = [PlayType.UPSELL, PlayType.ACQUISITION];
      if (!selectedPlayType) {
        return true;
      }
      if (regularTypes.includes(selectedPlayType)) {
        return x.play_type === selectedPlayType;
      }
      return !regularTypes.includes(x.play_type);
    },
    [selectedPlayType]
  );

  const filterByDeliveryType = useCallback(
    (p: Play) =>
      deliveryFilters.length
        ? deliveryFilters.some((f) => f.value === p.id)
        : true,
    [deliveryFilters]
  );

  const filteredPlayIds = useMemo(
    () =>
      playsList
        .filter((x) => filterBySelectedType(x) && filterByDeliveryType(x))
        .map((x) => x.id),
    [filterBySelectedType, filterByDeliveryType, playsList]
  );

  const { data: playMetaData, isFetching: isPlaysMetaDataFetching } =
    playSlice.endpoints.getPlaysMetaData.useQuery(
      {
        appKey,
        brandId,
        playIds: activePlayIds,
      },
      {
        skip: activePlayIds.length === 0,
      }
    );

  const isFetching =
    isPlaysFetching ||
    isArchivedPlaysFetching ||
    isPlaysMetaDataFetching ||
    isPlaysReportFetching ||
    isEntityReportFetching;

  const isLoading = isPlaysLoading || isArchivedPlaysLoading;

  return useMemo(
    () => ({
      playsList,
      archivedPlaysList,
      playMetaData,
      playMetrics,
      entityMetrics,
      isFetching,
      filteredPlayIds,
      selectedPlayType,
      showDemoData,
      playMetricsError,
      entityMetricsError,
      isLoading,
    }),
    [
      archivedPlaysList,
      isFetching,
      playMetaData,
      playsList,
      playMetrics,
      entityMetrics,
      filteredPlayIds,
      selectedPlayType,
      showDemoData,
      playMetricsError,
      entityMetricsError,
      isLoading,
    ]
  );
};
